import {
  getRequisitions as getRequisitionsService,
  getMyRequisitions as getMyRequisitionsService,
  getRequisitionDetails as getRequisitionDetailsService,
  createRequisition as createRequisitionService,
  updateRequisition as updateRequisitionService,
  deleteRequisition as deleteRequisitionService,
  approveRejectRequisition as approveRejectRequisitionService,
  getRequisitionMasterItems,
} from "services/company/requisition/requisitionServices";
import {
  getRequisitionsStart,
  getRequisitionsSuccess,
  getRequisitionsFailure,
  getMyRequisitionsSuccess,
  getRequisitionDetailsStart,
  getRequisitionDetailsSuccess,
  getRequisitionDetailsFailure,
  createRequisitionStart,
  createRequisitionSuccess,
  createRequisitionFailure,
  updateRequisitionStart,
  updateRequisitionSuccess,
  updateRequisitionFailure,
  deleteRequisitionStart,
  deleteRequisitionSuccess,
  deleteRequisitionFailure,
  approveRejectRequisitionStart,
  approveRejectRequisitionSuccess,
  approveRejectRequisitionFailure,
} from "./requisitionSlice";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";


const getRequisitionRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.requisitions)) return data.requisitions;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
};

// Fetch all requisitions
export const fetchRequisitions = () => async (dispatch) => {
  try {
    dispatch(getRequisitionsStart());
    const response = await getRequisitionsService();

    if (response?.meta?.status === true && response?.meta?.code === 200) {
      const rows = getRequisitionRows(response.data);
      dispatch(getRequisitionsSuccess(rows));
      return { success: true, data: rows };
    } else {
      const errorMsg =
        response?.meta?.message || "Failed to fetch requisitions";
      dispatch(getRequisitionsFailure(errorMsg));
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    console.error("Fetch requisitions error:", error);
    const errorMsg =
      error?.response?.data?.meta?.message ||
      error.message ||
      "Failed to fetch requisitions";
    dispatch(getRequisitionsFailure(errorMsg));
    showErrorToast(errorMsg);
    return { success: false, error: errorMsg };
  }
};

// Fetch requisitions created by myself
export const fetchMyRequisitions = () => async (dispatch) => {
  try {
    dispatch(getRequisitionsStart());
    const response = await getMyRequisitionsService();

    if (response?.meta?.status === true && response?.meta?.code === 200) {
      const rows = getRequisitionRows(response.data);
      dispatch(getMyRequisitionsSuccess(rows));
      return { success: true, data: rows };
    } else {
      const errorMsg =
        response?.meta?.message || "Failed to fetch my requisitions";
      dispatch(getRequisitionsFailure(errorMsg));
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    console.error("Fetch my requisitions error:", error);
    const errorMsg =
      error?.response?.data?.meta?.message ||
      error.message ||
      "Failed to fetch my requisitions";
    dispatch(getRequisitionsFailure(errorMsg));
    showErrorToast(errorMsg);
    return { success: false, error: errorMsg };
  }
};

// Fetch requisition details
export const fetchRequisitionDetails = (requisitionId) => async (dispatch) => {
  try {
    dispatch(getRequisitionDetailsStart(requisitionId));
    const response = await getRequisitionDetailsService(requisitionId);

    if (response?.meta?.status === true && response?.meta?.code === 200) {
      dispatch(
        getRequisitionDetailsSuccess({
          id: requisitionId,
          data: response.data,
        }),
      );
      return { success: true, data: response.data };
    } else {
      const errorMsg =
        response?.meta?.message || "Failed to fetch requisition details";
      dispatch(
        getRequisitionDetailsFailure({ id: requisitionId, error: errorMsg }),
      );
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    console.error("Fetch requisition details error:", error);
    const errorMsg =
      error?.response?.data?.meta?.message ||
      error?.meta?.message ||
      error?.message ||
      "Failed to fetch requisition details";
    dispatch(
      getRequisitionDetailsFailure({ id: requisitionId, error: errorMsg }),
    );
    showErrorToast(errorMsg);
    return { success: false, error: errorMsg };
  }
};

// Create a new requisition
export const createNewRequisition = (requisitionData) => async (dispatch) => {
  try {
    dispatch(createRequisitionStart());
    const response = await createRequisitionService(requisitionData);

    if (response?.meta?.status === true && response?.meta?.code === 200) {
      dispatch(createRequisitionSuccess(response.data));
      showSuccessToast(
        response?.meta?.message || "Requisition created successfully!",
      );
      return { success: true, data: response.data };
    } else {
      const errorMsg =
        response?.meta?.message || "Failed to create requisition";
      dispatch(createRequisitionFailure(errorMsg));
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    console.error("Create requisition error:", error);
    const errorMsg =
      error?.response?.data?.meta?.message ||
      error.message ||
      "Failed to create requisition";
    dispatch(createRequisitionFailure(errorMsg));
    showErrorToast(errorMsg);
    return { success: false, error: errorMsg };
  }
};

// Update existing requisition before acceptance/rejection
export const updateExistingRequisition =
  (requisitionId, requisitionData) => async (dispatch) => {
    try {
      dispatch(updateRequisitionStart(requisitionId));
      const response = await updateRequisitionService(
        requisitionId,
        requisitionData,
      );

      if (response?.meta?.status === true && response?.meta?.code === 200) {
        dispatch(
          updateRequisitionSuccess({
            id: requisitionId,
            data: response.data,
          }),
        );
        showSuccessToast(
          response?.meta?.message || "Requisition updated successfully!",
        );
        return { success: true, data: response.data };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to update requisition";
        dispatch(
          updateRequisitionFailure({
            id: requisitionId,
            error: errorMsg,
          }),
        );
        showErrorToast(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("Update requisition error:", error);
      const errorMsg =
        error?.response?.data?.meta?.message ||
        error?.meta?.message ||
        error?.message ||
        "Failed to update requisition";
      dispatch(
        updateRequisitionFailure({ id: requisitionId, error: errorMsg }),
      );
      return { success: false, error: errorMsg };
    }
  };

// Delete existing requisition before acceptance/rejection
export const deleteExistingRequisition =
  (requisitionId) => async (dispatch) => {
    try {
      dispatch(deleteRequisitionStart(requisitionId));
      const response = await deleteRequisitionService(requisitionId);

      if (response?.meta?.status === true && response?.meta?.code === 200) {
        dispatch(deleteRequisitionSuccess(requisitionId));
        showSuccessToast(
          response?.meta?.message || "Requisition deleted successfully!",
        );
        return { success: true, data: response.data };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to delete requisition";
        dispatch(
          deleteRequisitionFailure({
            id: requisitionId,
            error: errorMsg,
          }),
        );
        showErrorToast(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("Delete requisition error:", error);
      const errorMsg =
        error?.response?.data?.meta?.message ||
        error?.meta?.message ||
        error?.message ||
        "Failed to delete requisition";
      dispatch(
        deleteRequisitionFailure({ id: requisitionId, error: errorMsg }),
      );
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

// Approve or reject requisition

export const approveRejectExistingRequisition =
  (requisitionId, actionData) => async (dispatch) => {
    try {
      dispatch(approveRejectRequisitionStart(requisitionId));
      const response = await approveRejectRequisitionService(
        requisitionId,
        actionData,
      );

      if (response?.meta?.status === true && response?.meta?.code === 200) {
        dispatch(
          approveRejectRequisitionSuccess({
            id: requisitionId,
            data: response.data,
          }),
        );
        showSuccessToast(
          response?.meta?.message || "Requisition updated successfully!",
        );
        return { success: true, data: response.data };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to update requisition";
        dispatch(
          approveRejectRequisitionFailure({
            id: requisitionId,
            error: errorMsg,
          }),
        );
        showErrorToast(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("Approve/reject requisition error:", error);
      const errorMsg =
        error?.response?.data?.meta?.message ||
        error?.meta?.message ||
        error?.message ||
        "Failed to update requisition";
      dispatch(
        approveRejectRequisitionFailure({ id: requisitionId, error: errorMsg }),
      );
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

// Fetch requisition master items
export const fetchRequisitionMasterItems =
  (projectId = null) =>
    async (dispatch) => {
      try {
        const response = await getRequisitionMasterItems(projectId);

        if (response?.meta?.status === true && response?.meta?.code === 200) {
          return { success: true, data: response.data || [] };
        } else {
          const errorMsg = response?.meta?.message || "Failed to fetch items";
          showErrorToast(errorMsg);
          return { success: false, error: errorMsg };
        }
      } catch (error) {
        console.error("Fetch requisition master items error:", error);
        const errorMsg =
          error?.response?.data?.meta?.message ||
          error.message ||
          "Failed to fetch items";
        showErrorToast(errorMsg);
        return { success: false, error: errorMsg };
      }
    };

